import { useState, useRef, useEffect } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import {
  CalendarEventParams,
  downloadICSFile,
  getGoogleCalendarUrl,
  getOutlookCalendarUrl,
} from '../../utils/icsGenerator';

interface AddToCalendarButtonProps {
  event: CalendarEventParams;
  variant?: 'compact' | 'sidebar' | 'corner';
}

export default function AddToCalendarButton({
  event,
  variant = 'compact',
}: AddToCalendarButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleGoogle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(getGoogleCalendarUrl(event), '_blank', 'noopener');
    setOpen(false);
  };

  const handleOutlook = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(getOutlookCalendarUrl(event), '_blank', 'noopener');
    setOpen(false);
  };

  const handleICS = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    downloadICSFile(event);
    setOpen(false);
  };

  const isSidebar = variant === 'sidebar';
  const isCorner = variant === 'corner';

  return (
    <div ref={ref} className="relative">
      {isSidebar ? (
        <Button
          type="button"
          variant="primary"
          className="w-full h-12 text-sm px-3 border-2 border-transparent whitespace-nowrap"
          onClick={handleToggle}
          title="Add to Calendar"
          aria-expanded={open}
        >
          <CalendarDaysIcon className="h-5 w-5 mr-2 shrink-0" />
          Add to calendar
        </Button>
      ) : isCorner ? (
        <button
          type="button"
          onClick={handleToggle}
          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] text-primary focus-ring rounded-sm"
          aria-label="Add to Calendar"
          title="Add to Calendar"
          aria-expanded={open}
        >
          <CalendarDaysIcon className="h-5 w-5" aria-hidden />
        </button>
      ) : (
        <button
          onClick={handleToggle}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:text-primary transition-colors"
          title="Add to Calendar"
        >
          <CalendarDaysIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Add to Calendar</span>
        </button>
      )}

      {open && (
        <div
          className={`absolute top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 ${
            isSidebar ? 'left-0 right-0 w-full min-w-[200px]' : 'right-0 w-52'
          }`}
        >
          <button
            onClick={handleGoogle}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Google Calendar
          </button>
          <button
            onClick={handleOutlook}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
          >
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 22 22">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="12" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="12" width="9" height="9" fill="#00a4ef" />
              <rect x="12" y="12" width="9" height="9" fill="#ffb900" />
            </svg>
            Outlook
          </button>
          <div className="border-t border-gray-100 my-1" />
          <button
            onClick={handleICS}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
          >
            <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
            Download .ics file
          </button>
        </div>
      )}
    </div>
  );
}
